/**
 * FunShine Authentication & Profile Service
 * Implements frictionless Anonymous Authentication with local storage persistence.
 */
import { supabase, isSupabaseConfigured } from './supabase.js';

const LOCAL_PROFILE_KEY = 'funshine_player_profile';

const DEFAULT_AVATARS = ['🦊', '🐯', '🐼', '🐨', '🚀', '⭐', '⚡', '🔥', '🎲', '👑'];
const DEFAULT_COLORS = ['#f59e0b', '#06b6d4', '#10b981', '#f43f5e', '#8b5cf6', '#ec4899'];

class AuthService {
  constructor() {
    this.user = null;
    this.profile = null;
    this.listeners = new Set();
    this.initialized = false;
  }

  /**
   * Generates or retrieves local guest profile for offline or pre-auth play
   */
  getOrCreateLocalProfile() {
    try {
      const stored = localStorage.getItem(LOCAL_PROFILE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {}

    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    const newProfile = {
      id: 'local_' + Math.random().toString(36).substring(2, 10),
      display_name: `Player_${randomSuffix}`,
      avatar_emoji: DEFAULT_AVATARS[Math.floor(Math.random() * DEFAULT_AVATARS.length)],
      avatar_color: DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)],
      games_played: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      current_streak: 0,
      best_streak: 0,
      is_anonymous: true
    };

    try {
      localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(newProfile));
    } catch (e) {}

    return newProfile;
  }

  /**
   * Initializes session: attempts Supabase Anonymous Auth or falls back to local profile
   */
  async init() {
    if (this.initialized) return this.profile;

    if (!isSupabaseConfigured()) {
      this.profile = this.getOrCreateLocalProfile();
      this.initialized = true;
      this.notify();
      return this.profile;
    }

    try {
      // 1. Check existing session
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      let session = sessionData?.session;

      if (!session || sessionErr) {
        // Sign in anonymously
        const local = this.getOrCreateLocalProfile();
        const { data: authData, error: authErr } = await supabase.auth.signInAnonymously({
          options: {
            data: {
              display_name: local.display_name,
              avatar_emoji: local.avatar_emoji,
              avatar_color: local.avatar_color
            }
          }
        });

        if (authErr) {
          console.warn('[Auth] Anonymous sign-in failed, using local profile:', authErr.message);
          this.profile = this.getOrCreateLocalProfile();
          this.initialized = true;
          this.notify();
          return this.profile;
        }

        session = authData.session;
      }

      this.user = session.user;
      await this.fetchOrCreateProfile(this.user.id);
    } catch (err) {
      console.warn('[Auth] Initialization error, falling back to local profile:', err);
      this.profile = this.getOrCreateLocalProfile();
    }

    this.initialized = true;
    this.notify();
    return this.profile;
  }

  async fetchOrCreateProfile(userId) {
    const local = this.getOrCreateLocalProfile();

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (data && !error) {
        this.profile = data;
        localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(data));
        return data;
      }

      // Profile record does not exist yet; insert it
      const newProfile = {
        id: userId,
        display_name: local.display_name || `Player_${userId.substring(0, 5)}`,
        avatar_emoji: local.avatar_emoji || '🎮',
        avatar_color: local.avatar_color || '#f59e0b',
        games_played: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        current_streak: 0,
        best_streak: 0
      };

      const { data: inserted, error: insertErr } = await supabase
        .from('profiles')
        .insert(newProfile)
        .select()
        .single();

      if (inserted && !insertErr) {
        this.profile = inserted;
        localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(inserted));
        return inserted;
      }
    } catch (e) {
      console.warn('[Auth] Error fetching profile:', e);
    }

    this.profile = { ...local, id: userId };
    return this.profile;
  }

  async updateProfile(updates) {
    if (!this.profile) return null;

    const merged = { ...this.profile, ...updates };
    this.profile = merged;
    try {
      localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(merged));
    } catch (e) {}

    if (isSupabaseConfigured() && this.user) {
      try {
        await supabase
          .from('profiles')
          .update({
            display_name: merged.display_name,
            avatar_emoji: merged.avatar_emoji,
            avatar_color: merged.avatar_color,
            updated_at: new Date().toISOString()
          })
          .eq('id', this.user.id);
      } catch (err) {
        console.warn('[Auth] Failed to sync profile updates to remote:', err);
      }
    }

    this.notify();
    return this.profile;
  }

  recordLocalSoloResult(isWin, isDraw = false) {
    if (!this.profile) return;
    this.profile.games_played = (this.profile.games_played || 0) + 1;
    if (isWin) {
      this.profile.wins = (this.profile.wins || 0) + 1;
      this.profile.current_streak = (this.profile.current_streak || 0) + 1;
      this.profile.best_streak = Math.max(this.profile.best_streak || 0, this.profile.current_streak);
    } else if (isDraw) {
      this.profile.draws = (this.profile.draws || 0) + 1;
    } else {
      this.profile.losses = (this.profile.losses || 0) + 1;
      this.profile.current_streak = 0;
    }

    try {
      localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(this.profile));
    } catch (e) {}
    this.notify();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    if (this.profile) listener(this.profile);
    return () => this.listeners.delete(listener);
  }

  notify() {
    for (const listener of this.listeners) {
      listener(this.profile);
    }
  }

  getProfile() {
    return this.profile || this.getOrCreateLocalProfile();
  }

  getUserId() {
    return this.profile?.id || this.getOrCreateLocalProfile().id;
  }
}

export const auth = new AuthService();
